import { Navigate, Outlet, Link, NavLink } from "react-router-dom";
import {
  Loader2, ArrowLeft, LayoutDashboard, DollarSign, Users, GitBranch,
  Activity, AlertTriangle, Wrench, Undo2, Smartphone, Megaphone,
  ShieldAlert, Cloud,
} from "lucide-react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { cn } from "@/lib/utils";

const MOBILE_NAV = [
  { title: "Overview", url: "/admin/overview", icon: LayoutDashboard },
  { title: "Revenue", url: "/admin/revenue", icon: DollarSign },
  { title: "Users", url: "/admin/users", icon: Users },
  { title: "Funnel", url: "/admin/funnel", icon: GitBranch },
  { title: "Engagement", url: "/admin/engagement", icon: Activity },
  { title: "Stability", url: "/admin/stability", icon: AlertTriangle },
  { title: "AI & Storage", url: "/admin/operations", icon: Wrench },
  { title: "Refunds", url: "/admin/refunds", icon: Undo2 },
  { title: "Mobile", url: "/admin/mobile", icon: Smartphone },
  { title: "Broadcasts", url: "/admin/broadcasts", icon: Megaphone },
  { title: "Security", url: "/admin/security", icon: ShieldAlert },
  { title: "Cloud", url: "/admin/cloud", icon: Cloud },
];

export default function AdminLayout() {
  const isAdmin = useIsAdmin();

  if (isAdmin === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/app" replace />;

  return (
    <>
      <SEO title="Admin Console · FitAura AI" description="Internal admin console." noindex />
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AdminSidebar />
          <div className="flex min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-20 border-b border-white/5 bg-background/85 px-4 pt-[max(env(safe-area-inset-top),10px)] pb-3 backdrop-blur-xl">
              <div className="flex w-full items-center gap-2.5">
                <SidebarTrigger />
                <Button asChild size="icon" variant="ghost" className="h-9 w-9 shrink-0">
                  <Link to="/app/profile" aria-label="Back to app">
                    <ArrowLeft className="h-5 w-5" />
                  </Link>
                </Button>
                <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 ring-1 ring-primary/30 shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)]">
                  <img src="/app-icon-128.png" alt="FitAura AI" className="h-full w-full object-cover" />
                </span>
                <div className="min-w-0">
                  <span className="font-display block text-[15px] font-bold leading-none tracking-tight">
                    FitAura<span className="text-primary"> AI</span>
                  </span>
                  <span className="font-display mt-1 block truncate text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                    Admin Console
                  </span>
                </div>
              </div>
            </header>
            {/* Mobile-only horizontal nav so all admin sections are reachable without opening the sidebar sheet */}
            <nav className="-mx-px overflow-x-auto border-b border-white/10 bg-background/80 backdrop-blur-xl md:hidden">
              <ul className="flex w-max items-center gap-1 px-2 py-1.5">
                {MOBILE_NAV.map((item) => (
                  <li key={item.url}>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                          isActive
                            ? "bg-primary/15 text-primary"
                            : "text-muted-foreground hover:bg-white/5 hover:text-foreground",
                        )
                      }
                    >
                      <item.icon className="h-3.5 w-3.5" />
                      {item.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
            <main className="flex-1 overflow-x-hidden">
              <Outlet />
            </main>
          </div>
        </div>
      </SidebarProvider>
    </>
  );
}
